import {
  CreateProjectVersionInput,
  Project,
  ProjectVersion,
  UpdateProjectVersionInput,
} from "../../API";
import { generateClient } from "aws-amplify/api";
import {
  createProjectVersion,
  updateProject,
  updateProjectVersion,
} from "../../graphql/mutations";
import { insertIntoGraphQlString } from "modifygraphqlstring";
import { getProject, getProjectVersion } from "../../graphql/queries";
import { customGetProject } from "../../graphql/customQueries";
import Observable from "zen-observable";
import { ProjectBasicInfo } from "../projectStore";
import { getAccessTokenForLambdaAuth } from "../../utils/authHelpers"; // Graphql Client
const client = generateClient();

type ProjectMinimalInfo = {
  title: string;
  id: string;
  updatedAt: string;
  createdAt: string;
};
export async function createVersion(project: Project) {
  const input: CreateProjectVersionInput = {
    id: project.id,
    accountOwner: isProjectVersion(project)
      ? (project as ProjectVersion).accountOwner
      : project.account.id,
    settings: project.settings,
    settingsJsonSchemaStr: project.settingsJsonSchemaStr,
    stateJsonSchemaStr: project.stateJsonSchemaStr,
    projectProjectVersionsId: project.id,
    videoParts: project.videoParts?.map((part) => {
      const { __typename, ...rest } = part;
      return rest;
    }),
  };

  const modifiedCreateProjectVersion = insertIntoGraphQlString(
    insertIntoGraphQlString(createProjectVersion, {
      path: ["videoParts"],
      key: "modsArr",
      value: {
        id: true,
        dataStr: true,
        name: true,
      },
    }),
    {
      path: ["videoParts"],
      key: "fonts",
      value: {
        family: true,
        style: true,
        url: true,
        weight: true,
      },
    }
  );
  const authToken = await getAccessTokenForLambdaAuth();
  const {
    data: { createProjectVersion: projectVersion },
  } = (await client.graphql({
    query: modifiedCreateProjectVersion,
    variables: {
      input,
    },
    authMode: "lambda",
    authToken,
  })) as { data: { createProjectVersion: ProjectVersion } };
  return projectVersion;
}

export async function updateVersion(project: UpdateProjectVersionInput) {
  const input = {
    id: project.id,
    ...(project?.settings && { settings: project.settings }),
    ...(project?.settingsJsonSchemaStr && {
      settingsJsonSchemaStr: project.settingsJsonSchemaStr,
    }),
    ...(project?.stateJsonSchemaStr && {
      stateJsonSchemaStr: project.stateJsonSchemaStr,
    }),
    ...(project?.videoParts && { videoParts: project.videoParts }),
  };
  const modifiedUpdateProject = insertIntoGraphQlString(
    insertIntoGraphQlString(updateProjectVersion, {
      path: ["videoParts"],
      key: "modsArr",
      value: {
        id: true,
        dataStr: true,
        origin: true,
        name: true,
        description:true
      },
    }),
    {
      path: ["videoParts"],
      key: "fonts",
      value: {
        family: true,
        style: true,
        url: true,
        weight: true,
      },
    }
  );
  const authToken = await getAccessTokenForLambdaAuth();
  return (await client.graphql({
    query: modifiedUpdateProject,
    variables: { input },
    authMode: "lambda",
    authToken,
  })) as { data: ProjectVersion };
}

export async function getPublishedProject(id: string) {
  // Use customGetProject which already includes:
  // - videoParts with modsArr and fonts
  // - flowDiagram with full nodes and edges structure
  // - excludes unauthorized fields (formData, renders, notifications)
  const authToken = await getAccessTokenForLambdaAuth();
  const {
    data: { getProject: project },
  } = (await client.graphql({
    query: customGetProject,
    variables: { id },
    authMode: "lambda",
    authToken,
  })) as { data: { getProject: Project } };
  return project;
}

export async function getPublishedProjectBasicInfo(id: string) {
  const getProject = `
  query GetProject($id: ID!) {
    getProject(id: $id) {
      id
      title
      updatedAt
      createdAt
    }
  }
`;

  const authToken = await getAccessTokenForLambdaAuth();
  const {
    data: { getProject: project },
  } = (await client.graphql({
    query: getProject,
    variables: { id },
    authMode: "lambda",
    authToken,
  })) as { data: { getProject: ProjectMinimalInfo } };

  return project;
}

export async function getLatestProjectVersion(id: string) {
  // TODO add logic for fetching latest version when we have multiple versions per project id
  const modifiedGetProjectVersion = insertIntoGraphQlString(
    insertIntoGraphQlString(getProjectVersion, {
      path: ["videoParts"],
      key: "modsArr",
      value: {
        id: true,
        dataStr: true,
        origin: true,
        name: true,
        description:true
      },
    }),
    {
      path: ["videoParts"],
      key: "fonts",
      value: {
        family: true,
        style: true,
        url: true,
        weight: true,
      },
    }
  );
  const authToken = await getAccessTokenForLambdaAuth();
  const {
    data: { getProjectVersion: projectVersion },
  } = (await client.graphql({
    query: modifiedGetProjectVersion,
    variables: { id },
    authMode: "lambda",
    authToken,
  })) as { data: { getProjectVersion: ProjectVersion } };
  return projectVersion;
}

function isProjectVersion(
  project: Project | ProjectVersion
): project is ProjectVersion {
  return (project as ProjectVersion).accountOwner !== undefined;
}
export const saveDynamicThumbnail = async (
  projectId: string,
  thumbnailId: string
) => {
  const authToken = await getAccessTokenForLambdaAuth();
  const dk = await client.graphql({
    query: updateProject,
    variables: {
      input: {
        id: projectId,
        dynamicThumbnails: [
          { id: thumbnailId, updatedAt: new Date().toISOString() },
        ],
      },
    },
    authMode: "lambda",
    authToken,
  });
};
export const listProjectBasicInfo = async () => {
  const listProjectBasicInfoQuery = `query ListProjects(
    $filter: ModelProjectFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listProjects(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        description
        updatedAt
        projectAccountId
        createdAt
        thumbS3Url
      }
      nextToken
    }
  }`;
  let nextToken = null;
  const projects: ProjectBasicInfo[] = [];
  const authToken = await getAccessTokenForLambdaAuth();
  do {
    const projectsData = (await client.graphql({
      query: listProjectBasicInfoQuery,
      variables: { nextToken },
      authMode: "lambda",
      authToken,
    })) as { data: any };
    nextToken = projectsData?.data?.listProjects?.nextToken;
    if (projectsData?.data?.listProjects?.items) {
      projects.push(...projectsData.data.listProjects.items);
    }
  } while (nextToken);
  return projects;
};
